import { useEffect, useMemo, useState } from 'react';
import { Chip, type ChipColor } from '@jotpsych/ui';
import { Download, Refresh } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  type ExportFormat,
  type ExportStatusResponse,
  useLazyDownloadOrgExportQuery,
  useListOrgExportsQuery,
  useStartOrgExportMutation,
} from '../store/hooks';

const TERMINAL_STATUSES = new Set(['completed', 'failed']);
const ACTIVE_POLL_MS = 3000;

/** Map an export status onto a soft status-chip color. */
function statusColor(status: string): ChipColor {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'processing':
      return 'primary';
    default:
      return 'default'; // pending / unknown
  }
}

function statusLabel(status: string): string {
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function formatBytes(bytes?: number | null): string {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

interface DataExportSectionProps {
  organizationId: string;
}

/**
 * Admin surface for whole-organization data export (GDN-1983): pick a format,
 * start a background export, watch each one progress, and download the finished
 * ZIP. The mirror of the Data Import tab — trigger-based (no upload). The list
 * endpoint carries each export's live status, so the UI polls the list while
 * any export is in flight and stops once they are all terminal.
 */
export function DataExportSection({ organizationId }: DataExportSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [format, setFormat] = useState<ExportFormat>('fhir');
  const [pollMs, setPollMs] = useState(0);

  const [startExport, { isLoading: isStarting }] = useStartOrgExportMutation();
  const [triggerDownload, { isFetching: isDownloading }] = useLazyDownloadOrgExportQuery();
  const listQuery = useListOrgExportsQuery({ organizationId }, { pollingInterval: pollMs });

  const exports = useMemo<ExportStatusResponse[]>(
    () => listQuery.data?.items ?? [],
    [listQuery.data],
  );

  useEffect(() => {
    const active = exports.some(exp => !TERMINAL_STATUSES.has(exp.status));
    setPollMs(active ? ACTIVE_POLL_MS : 0);
  }, [exports]);

  const handleStart = async () => {
    try {
      await startExport({ organizationId, startExportRequest: { format } }).unwrap();
      enqueueSnackbar('Export started', { variant: 'success' });
      setPollMs(ACTIVE_POLL_MS);
      void listQuery.refetch();
    } catch {
      enqueueSnackbar('Failed to start export', { variant: 'error' });
    }
  };

  const handleDownload = async (exportId: string) => {
    try {
      const { download_url } = await triggerDownload({ organizationId, exportId }).unwrap();
      window.open(download_url, '_blank', 'noopener,noreferrer');
    } catch {
      enqueueSnackbar('Could not generate a download link', { variant: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Export this organization&apos;s clinical data. Each export runs in the
        background and produces a single encrypted ZIP (per-patient clinical
        documents plus note PDFs) you can download once it completes.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardHeader title="Start an export" />
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="export-format-label">Format</InputLabel>
              <Select
                labelId="export-format-label"
                label="Format"
                value={format}
                onChange={event => setFormat(event.target.value)}
              >
                <MenuItem value="fhir">FHIR R4 Bundle (JSON)</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={() => void handleStart()}
              disabled={isStarting}
              startIcon={
                isStarting ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              {isStarting ? 'Starting…' : 'Start export'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title="Exports"
          subheader="Most recent first. In-flight exports refresh automatically."
          action={
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => void listQuery.refetch()}
                aria-label="Refresh exports"
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          }
        />
        <CardContent>
          {listQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : exports.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No exports yet. Start one above.
            </Typography>
          ) : (
            <Table size="small" aria-label="Organization data exports">
              <TableHead>
                <TableRow>
                  <TableCell>Created</TableCell>
                  <TableCell>Requested by</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell align="right">Size</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {exports.map(exp => {
                  const isProcessing = exp.status === 'processing';
                  const isCompleted = exp.status === 'completed';
                  return (
                    <TableRow key={exp.export_id} hover>
                      <TableCell>
                        {exp.created_at ? new Date(exp.created_at).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell>
                        {exp.requested_by_name ?? exp.requested_by_user_id ?? '—'}
                      </TableCell>
                      <TableCell>{exp.format ? exp.format.toUpperCase() : '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="soft"
                          color={statusColor(exp.status)}
                          label={statusLabel(exp.status)}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        {isProcessing ? (
                          <Box>
                            <LinearProgress
                              variant="determinate"
                              value={exp.percentage ?? 0}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {exp.processed_count ?? 0} / {exp.total_count ?? 0} patients
                            </Typography>
                          </Box>
                        ) : isCompleted ? (
                          <Typography variant="caption" color="text.secondary">
                            {exp.exported_count ?? 0} exported
                            {exp.failed_count ? `, ${exp.failed_count} failed` : ''}
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right">{formatBytes(exp.output_size_bytes)}</TableCell>
                      <TableCell align="right">
                        {isCompleted && (
                          <Button
                            size="small"
                            startIcon={<Download fontSize="small" />}
                            onClick={() => void handleDownload(exp.export_id)}
                            disabled={isDownloading}
                          >
                            Download
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
